import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { TourProvider } from "@/contexts/TourContext";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { PageLoader } from "@/components/shared/PageLoader";
import { CookieConsentBanner } from "@/components/shared/CookieConsentBanner";

// Globaler Error Handler für unbehandelte Fehler
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    console.error("[Global] Unhandled error:", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    console.error("[Global] Unhandled promise rejection:", {
      reason: event.reason,
      timestamp: new Date().toISOString(),
    });
  });
}

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Stammdaten = lazy(() => import("./pages/Stammdaten"));
const Szenarien = lazy(() => import("./pages/Szenarien"));
const SzenarioNeu = lazy(() => import("./pages/SzenarioNeu"));
const SzenarioDetail = lazy(() => import("./pages/SzenarioDetail"));
const SzenarioErgebnisse = lazy(() => import("./pages/SzenarioErgebnisse"));
const SzenarioVergleich = lazy(() => import("./pages/SzenarioVergleich"));
const SzenarioEntscheidung = lazy(() => import("./pages/SzenarioEntscheidung"));
const SzenarioOutcome = lazy(() => import("./pages/SzenarioOutcome"));
const Einstellungen = lazy(() => import("./pages/Einstellungen"));
const CompanyContextSettings = lazy(() => import("./pages/CompanyContextSettings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Benchmarks = lazy(() => import("./pages/Benchmarks"));
const CostScenarios = lazy(() => import("./pages/CostScenarios"));
const CostScenarioNew = lazy(() => import("./pages/CostScenarioNew"));
const CostDrivers = lazy(() => import("./pages/CostDrivers"));
const CostDriverSources = lazy(() => import("./pages/CostDriverSources"));
const PricingNavigator = lazy(() => import("./pages/PricingNavigator"));
const Optimize = lazy(() => import("./pages/Optimize"));
const OptimizeResults = lazy(() => import("./pages/OptimizeResults"));
const Webhooks = lazy(() => import("./pages/Webhooks"));
const Imports = lazy(() => import("./pages/Imports"));
const SftpConnections = lazy(() => import("./pages/SftpConnections"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WhiteLabelSettings = lazy(() => import("./pages/WhiteLabelSettings"));
const ApiSettings = lazy(() => import("./pages/ApiSettings"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const AdminCompanies = lazy(() => import("./pages/AdminCompanies"));
const Hilfe = lazy(() => import("./pages/Hilfe"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Signup = lazy(() => import("./pages/Signup"));
const Demo = lazy(() => import("./pages/Demo"));
const Contact = lazy(() => import("./pages/Contact"));
const Datenschutzerklaerung = lazy(() => import("./pages/Datenschutzerklaerung"));
const Impressum = lazy(() => import("./pages/Impressum"));
const DatenschutzBenchmarks = lazy(() => import("./pages/DatenschutzBenchmarks"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 60 * 1000,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <ImpersonationProvider>
              <BrandingProvider>
                <TourProvider>
                  <ImpersonationBanner />
                  <CookieConsentBanner />
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/demo" element={<Demo />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/stammdaten" element={<Stammdaten />} />
                      <Route path="/szenarien" element={<Szenarien />} />
                      <Route path="/szenarien/neu" element={<SzenarioNeu />} />
                      <Route path="/szenarien/:id" element={<SzenarioDetail />} />
                      <Route path="/szenarien/:id/ergebnisse" element={<SzenarioErgebnisse />} />
                      <Route path="/szenarien/:id/outcome" element={<SzenarioOutcome />} />
                      <Route path="/szenarien/vergleich" element={<SzenarioVergleich />} />
                      <Route path="/szenarien/:id/entscheidung" element={<SzenarioEntscheidung />} />
                      <Route path="/einstellungen" element={<Einstellungen />} />
                      <Route path="/settings/company-context" element={<CompanyContextSettings />} />
                      <Route path="/settings/white-label" element={<WhiteLabelSettings />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                      <Route path="/alerts" element={<Alerts />} />
                      <Route path="/benchmarks" element={<Benchmarks />} />
                      <Route path="/cost-scenarios" element={<CostScenarios />} />
                      <Route path="/cost-scenarios/new" element={<CostScenarioNew />} />
                      <Route path="/cost-drivers" element={<CostDrivers />} />
                      <Route path="/settings/cost-driver-sources" element={<CostDriverSources />} />
                      <Route path="/pricing-navigator" element={<PricingNavigator />} />
                      <Route path="/optimize" element={<Optimize />} />
                      <Route path="/optimize/:id" element={<OptimizeResults />} />
                      <Route path="/settings/webhooks" element={<Webhooks />} />
                      <Route path="/settings/imports" element={<Imports />} />
                      <Route path="/settings/sftp" element={<SftpConnections />} />
                      <Route path="/settings/api" element={<ApiSettings />} />
                      <Route path="/docs/api" element={<ApiDocs />} />
                      <Route path="/admin/companies" element={<AdminCompanies />} />
                      <Route path="/hilfe" element={<Hilfe />} />
                      <Route path="/datenschutz" element={<Datenschutzerklaerung />} />
                      <Route path="/impressum" element={<Impressum />} />
                      <Route path="/datenschutz-benchmarks" element={<DatenschutzBenchmarks />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </TourProvider>
              </BrandingProvider>
            </ImpersonationProvider>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
